import { useMemo } from 'react';

import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';

import { MobileAwareWebsiteLink } from 'components/MobileAwareWebsiteLink/MobileAwareWebsiteLink';

import c from './_profileSocials.module.scss';

export interface ProfileSocialsProps {
  tripadvisor?: string | null;
  yelp?: string | null;
  youtube?: string | null;
  instagram?: string | null;
  facebook?: string | null;
  twitter?: string | null;
  pinterest?: string | null;
  // socialmedia is a generic fallback, rather than a collection or specific social media platform. It is rendered last, after the other social media links.
  socialMedia?: string | null;
}

interface SocialLink {
  label: string;
  url: string;
  icon?: IconProp;
}

function toHttpsUrl(value: string): string {
  return /^(?:f|ht)tps?:\/\//i.test(value) ? value : `https://${value}`;
}

export function ProfileSocials({
  tripadvisor,
  yelp,
  youtube,
  instagram,
  facebook,
  twitter,
  pinterest,
  socialMedia,
}: ProfileSocialsProps) {
  const socials = useMemo((): SocialLink[] => {
    const result: SocialLink[] = [];
    if (tripadvisor) {
      result.push({
        label: i18n.translate('directoryProfileView.socialTripadvisor') as string,
        url: toHttpsUrl(tripadvisor),
      });
    }
    if (yelp) {
      result.push({
        label: i18n.translate('directoryProfileView.socialYelp') as string,
        url: toHttpsUrl(yelp),
        icon: ['fab', 'yelp'],
      });
    }
    if (youtube) {
      result.push({
        label: i18n.translate('directoryProfileView.socialYoutube') as string,
        url: toHttpsUrl(youtube),
        icon: ['fab', 'youtube'],
      });
    }
    if (instagram) {
      result.push({
        label: i18n.translate('directoryProfileView.socialInstagram') as string,
        url: toHttpsUrl(instagram),
        icon: ['fab', 'instagram'],
      });
    }
    if (facebook) {
      result.push({
        label: i18n.translate('directoryProfileView.socialFacebook') as string,
        url: toHttpsUrl(facebook),
        icon: ['fab', 'facebook'],
      });
    }
    if (twitter) {
      result.push({
        label: i18n.translate('directoryProfileView.socialTwitter') as string,
        url: toHttpsUrl(twitter),
        icon: ['fab', 'x-twitter'],
      });
    }
    if (pinterest) {
      result.push({
        label: i18n.translate('directoryProfileView.socialPinterest') as string,
        url: toHttpsUrl(pinterest),
        icon: ['fab', 'pinterest'],
      });
    }
    if (socialMedia) {
      result.push({
        label: i18n.translate('directoryProfileView.socialMedia') as string,
        url: toHttpsUrl(socialMedia),
        icon: ['fas', 'link'],
      });
    }
    return result;
  }, [tripadvisor, yelp, youtube, instagram, facebook, twitter, pinterest, socialMedia]);

  if (socials.length === 0) return null;

  return (
    <div className={clsx('profile-socials', c.wrapper)}>
      <div className={clsx('profile-socials__title', c.title)}>
        {i18n.translate('directoryProfileView.socialsTitle')}
      </div>
      {socials.map(({ label, url, icon }) => {
        return (
          <MobileAwareWebsiteLink key={label} url={url} className={clsx('profile-socials__social', c.social)}>
            {icon && <FontAwesomeIcon icon={icon} className={c.icon} />}
            {label}
          </MobileAwareWebsiteLink>
        );
      })}
    </div>
  );
}
